/**
 * Trash Bin Boss — Product / Buy section behavior
 * Handles: gallery thumbnail swap, quantity stepper,
 * variant selection -> price/availability sync, and
 * AJAX add-to-cart (Buy Now redirects straight to /cart).
 *
 * Depends on Shopify's global `Shopify.routes.root` for the
 * cart AJAX endpoint. No external libraries required.
 */
(function () {
  'use strict';

  function moneyFormat(cents, format) {
    // Fallback formatter if theme's Shopify.formatMoney isn't available.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function initProductSection(root) {
    var form = root.querySelector('[data-tbb-product-form]');
    if (!form) return;

    var variantIdInput = form.querySelector('[data-tbb-variant-id]');
    var priceEl = root.querySelector('[data-tbb-price]');
    var comparePriceEl = root.querySelector('[data-tbb-compare-price]');
    var addBtn = root.querySelector('[data-tbb-add-cart]');
    var buyBtn = root.querySelector('[data-tbb-buy-now]');
    var addBtnText = root.querySelector('[data-tbb-add-cart-text]');
    var errorEl = root.querySelector('[data-tbb-form-error]');
    var qtyInput = root.querySelector('[data-tbb-qty-input]');
    var qtyMinus = root.querySelector('[data-tbb-qty-minus]');
    var qtyPlus = root.querySelector('[data-tbb-qty-plus]');
    var optionInputs = Array.prototype.slice.call(root.querySelectorAll('[data-tbb-option-input]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-tbb-thumb]'));
    var mainImg = root.querySelector('[data-tbb-gallery-main]');

    var productJsonScript = root.querySelector('[data-tbb-product-json]');
    var productData = null;
    if (productJsonScript) {
      try {
        productData = JSON.parse(productJsonScript.textContent);
      } catch (e) {
        productData = null;
      }
    }

    // ---------- Gallery ----------
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
        if (mainImg) {
          mainImg.src = thumb.getAttribute('data-full');
          mainImg.alt = thumb.getAttribute('data-alt') || mainImg.alt;
        }
      });
    });

    // ---------- Quantity stepper ----------
    function clampQty(val) {
      var n = parseInt(val, 10);
      if (isNaN(n) || n < 1) n = 1;
      if (n > 99) n = 99;
      return n;
    }

    if (qtyMinus && qtyInput) {
      qtyMinus.addEventListener('click', function () {
        qtyInput.value = clampQty(parseInt(qtyInput.value, 10) - 1);
      });
    }
    if (qtyPlus && qtyInput) {
      qtyPlus.addEventListener('click', function () {
        qtyInput.value = clampQty(parseInt(qtyInput.value, 10) + 1);
      });
    }
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        qtyInput.value = clampQty(qtyInput.value);
      });
    }

    // ---------- Variant selection ----------
    function getSelectedOptions() {
      var selected = [];
      optionInputs.forEach(function (input) {
        if (input.checked) {
          selected[parseInt(input.getAttribute('data-option-index'), 10)] = input.value;
        }
      });
      return selected;
    }

    function findMatchingVariant(options) {
      if (!productData || !productData.variants) return null;
      return productData.variants.find(function (variant) {
        return variant.options.every(function (optionValue, index) {
          return options[index] === undefined || options[index] === optionValue;
        });
      });
    }

    function updateForVariant(variant) {
      if (!variant) {
        if (addBtn) { addBtn.setAttribute('disabled', 'disabled'); }
        if (buyBtn) { buyBtn.setAttribute('disabled', 'disabled'); }
        if (addBtnText) { addBtnText.textContent = 'Unavailable'; }
        return;
      }

      if (variantIdInput) variantIdInput.value = variant.id;

      if (priceEl) priceEl.textContent = moneyFormat(variant.price, window.theme && window.theme.moneyFormat);

      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = moneyFormat(variant.compare_at_price, window.theme && window.theme.moneyFormat);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      if (variant.available) {
        if (addBtn) { addBtn.removeAttribute('disabled'); }
        if (buyBtn) { buyBtn.removeAttribute('disabled'); }
        if (addBtnText) { addBtnText.textContent = 'Add to Cart'; }
      } else {
        if (addBtn) { addBtn.setAttribute('disabled', 'disabled'); }
        if (buyBtn) { buyBtn.setAttribute('disabled', 'disabled'); }
        if (addBtnText) { addBtnText.textContent = 'Sold Out'; }
      }

      if (variant.featured_image && mainImg) {
        mainImg.src = variant.featured_image.src.replace(/(\.[a-zA-Z]{3,4})(\?.*)?$/, '_900x$1$2');
      }
    }

    optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var options = getSelectedOptions();
        var variant = findMatchingVariant(options);

        // Update unavailable-looking labels for combos that don't exist
        optionInputs.forEach(function (otherInput) {
          var testOptions = options.slice();
          testOptions[parseInt(otherInput.getAttribute('data-option-index'), 10)] = otherInput.value;
          var testVariant = findMatchingVariant(testOptions);
          var label = root.querySelector('label[for="' + otherInput.id + '"]');
          if (label) {
            if (!testVariant) {
              label.setAttribute('data-unavailable', 'true');
            } else {
              label.removeAttribute('data-unavailable');
            }
          }
        });

        updateForVariant(variant);
      });
    });

    // ---------- Add to cart (AJAX) ----------
    function postToCart(formData, redirectToCart) {
      if (errorEl) {
        errorEl.classList.remove('is-visible');
        errorEl.textContent = '';
      }

      fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData)
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function () {
          if (redirectToCart) {
            window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart';
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            // Try to open a standard Dawn cart drawer if present.
            var cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else if (cartDrawer) {
              cartDrawer.classList.add('active', 'animate');
            } else {
              window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart';
            }
          }
        })
        .catch(function (err) {
          if (errorEl) {
            errorEl.textContent = (err && err.description) || 'Something went wrong adding this to your cart. Please try again.';
            errorEl.classList.add('is-visible');
          }
        });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var submitter = event.submitter || document.activeElement;
      var isBuyNow = submitter === buyBtn;

      var formData = {
        items: [
          {
            id: variantIdInput ? parseInt(variantIdInput.value, 10) : null,
            quantity: qtyInput ? clampQty(qtyInput.value) : 1
          }
        ]
      };

      postToCart(formData, isBuyNow);
    });
  }

  function init() {
    var sections = document.querySelectorAll('[data-tbb-product-section]');
    sections.forEach(initProductSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when Shopify theme editor swaps section HTML.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-tbb-product-section]');
    if (section) initProductSection(section);
  });
})();
